import { useState, useEffect, createContext, useContext, useRef } from "react";
import { http } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Video, User, Mail, Phone, LogOut, Home, FileText, MessageSquare, Target, BookOpen, CreditCard, Settings, ArrowRight, X, Check, Loader2, PenLine, AlertCircle, Send } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const ClientCtx = createContext(null);

function useClientAuth() {
  const [client, setClient] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("client_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    http.get("/client/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setClient(r.data))
      .catch(() => { localStorage.removeItem("client_token"); setToken(null); })
      .finally(() => setLoading(false));
  }, [token]);

  const login = async (email, password) => {
    const { data } = await http.post("/client/login", { email, password });
    localStorage.setItem("client_token", data.token);
    setToken(data.token);
    setClient(data.client);
    return data;
  };

  const register = async (body) => {
    const { data } = await http.post("/client/register", body);
    localStorage.setItem("client_token", data.token);
    setToken(data.token);
    setClient(data.client);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("client_token");
    setToken(null);
    setClient(null);
  };

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  return { client, token, loading, login, register, logout, authHeaders };
}

export default function ClientPortal() {
  const auth = useClientAuth();
  const [tab, setTab] = useState("dashboard");

  if (auth.loading) return <Layout><PageWrap><Loader2 className="text-[#D4AF37] animate-spin mx-auto mt-40" /></PageWrap></Layout>;

  if (!auth.client) return <Layout><PageWrap><AuthScreen auth={auth} /></PageWrap></Layout>;

  const NAV = [
    { key: "dashboard", label: "Dashboard", icon: <Home size={16} /> },
    { key: "appointments", label: "Appointments", icon: <Calendar size={16} /> },
    { key: "workshops", label: "My Workshops", icon: <BookOpen size={16} /> },
    { key: "forms", label: "Forms", icon: <FileText size={16} /> },
    { key: "goals", label: "Goals", icon: <Target size={16} /> },
    { key: "resources", label: "Resources", icon: <BookOpen size={16} /> },
    { key: "messages", label: "Messages", icon: <MessageSquare size={16} /> },
    { key: "profile", label: "Profile", icon: <User size={16} /> },
  ];

  return (
    <Layout>
      <PageWrap>
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37]">Client Portal</p>
              <h1 className="font-mc-h text-2xl text-[#F5F5F0]">Welcome back, {auth.client.preferred_name || auth.client.name?.split(" ")[0]}</h1>
            </div>
            <button onClick={auth.logout} className="flex items-center gap-2 text-zinc-600 hover:text-red-400 font-mc-b text-xs transition-colors" data-testid="portal-logout">
              <LogOut size={14} /> Sign Out
            </button>
          </div>

          {/* Nav */}
          <div className="flex gap-2 mb-8 flex-wrap">
            {NAV.map(n => (
              <button key={n.key} onClick={() => setTab(n.key)} data-testid={`portal-tab-${n.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-mc-b text-xs tracking-wide transition-all ${
                  tab === n.key ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-500 hover:border-[#D4AF37]/30"
                }`}>
                {n.icon} {n.label}
              </button>
            ))}
          </div>

          <ClientCtx.Provider value={auth}>
            {tab === "dashboard" && <Dashboard />}
            {tab === "appointments" && <Appointments />}
            {tab === "workshops" && <MyWorkshops />}
            {tab === "forms" && <ClientForms />}
            {tab === "goals" && <ClientGoals />}
            {tab === "resources" && <ClientResources />}
            {tab === "messages" && <ClientMessages />}
            {tab === "profile" && <Profile />}
          </ClientCtx.Provider>
        </div>
      </PageWrap>
    </Layout>
  );
}

/* ═══ AUTH SCREEN ═══ */
function AuthScreen({ auth }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", preferred_name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await auth.login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        if (!form.name || !form.email || !form.password) { toast.error("Name, email, and password are required."); setLoading(false); return; }
        if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); setLoading(false); return; }
        await auth.register(form);
        toast.success("Account created!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed.");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto pt-12">
      <div className="text-center mb-10">
        <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-3">Mirror Conversations&trade;</p>
        <h1 className="font-mc-h text-3xl text-[#F5F5F0] mb-2">Client Portal</h1>
        <p className="font-mc-b text-sm text-zinc-500">{mode === "login" ? "Sign in to view your appointments and resources." : "Create your account to get started."}</p>
      </div>

      <div className="flex gap-2 justify-center mb-8">
        <button onClick={() => setMode("login")} className={`px-6 py-2 rounded-full font-mc-b text-xs tracking-wide transition-all ${mode === "login" ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-500"}`} data-testid="auth-login-tab">Sign In</button>
        <button onClick={() => setMode("register")} className={`px-6 py-2 rounded-full font-mc-b text-xs tracking-wide transition-all ${mode === "register" ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-500"}`} data-testid="auth-register-tab">Create Account</button>
      </div>

      <form onSubmit={submit} className="space-y-5">
        {mode === "register" && (
          <>
            <div>
              <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Full Name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" data-testid="register-name" />
            </div>
            <div>
              <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Preferred Name</label>
              <input value={form.preferred_name} onChange={e => setForm({ ...form, preferred_name: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" />
            </div>
          </>
        )}
        <div>
          <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Email *</label>
          <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" data-testid="auth-email" />
        </div>
        <div>
          <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Password *</label>
          <input required type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" data-testid="auth-password" />
        </div>
        <button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-full font-mc-b tracking-wide uppercase text-sm flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50 mt-4" data-testid="auth-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          {mode === "login" ? "Sign In" : "Create Account"}
        </button>
      </form>

      <p className="text-center mt-8 font-mc-b text-xs text-zinc-600">
        {mode === "login" ? "Don't have an account? " : "Already have an account? "}
        <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#D4AF37] hover:underline">{mode === "login" ? "Create one" : "Sign in"}</button>
      </p>
    </div>
  );
}

/* ═══ DASHBOARD ═══ */
function Dashboard() {
  const { authHeaders } = useContext(ClientCtx);
  const [data, setData] = useState(null);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      http.get("/client/dashboard", { headers: authHeaders }),
      http.get("/forms/my", { headers: authHeaders }).catch(() => ({ data: [] })),
    ])
      .then(([d, f]) => { setData(d.data); setForms(f.data || []); })
      .catch(() => toast.error("Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="text-[#D4AF37] animate-spin mx-auto mt-20" />;
  if (!data) return null;

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-6">
      {/* Next Appointment */}
      {data.upcoming_appointments?.length > 0 ? (
        <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#D4AF37]/[0.03] p-6" data-testid="next-appointment">
          <p className="font-mc-b text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Next Appointment</p>
          <AptCard apt={data.upcoming_appointments[0]} />
          {data.upcoming_appointments.length > 1 && (
            <p className="font-mc-b text-xs text-zinc-600 mt-3">+ {data.upcoming_appointments.length - 1} more upcoming</p>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 text-center">
          <p className="font-mc-h text-xl text-[#F5F5F0] mb-3">No upcoming appointments</p>
          <Link to="/book" className="text-[#D4AF37] font-mc-b text-sm underline underline-offset-4">Book a Conversation</Link>
        </div>
      )}

      {/* Recent Appointments */}
      {data.past_appointments?.length > 0 && (
        <div>
          <h3 className="font-mc-b text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Recent Sessions</h3>
          <div className="space-y-2">
            {data.past_appointments.map(a => <AptCard key={a.id} apt={a} compact />)}
          </div>
        </div>
      )}

      {/* Pending Forms */}
      {forms.filter(f => f.status !== "completed").length > 0 && (
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.02] p-5" data-testid="pending-forms-banner">
          <p className="font-mc-b text-xs tracking-[0.2em] uppercase text-amber-400 mb-2">Forms Awaiting Completion</p>
          <div className="space-y-2">
            {forms.filter(f => f.status !== "completed").map(f => (
              <div key={f.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-amber-400" />
                  <span className="font-mc-b text-sm text-[#F5F5F0]">{f.template_title}</span>
                </div>
                <span className="font-mc-b text-[10px] text-amber-400 uppercase">Needs attention</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unread Messages */}
      {data.messages?.filter(m => m.sender === "admin" && !m.read).length > 0 && (
        <div className="rounded-2xl border border-blue-500/20 bg-blue-500/[0.02] p-5" data-testid="unread-messages-banner">
          <p className="font-mc-b text-xs tracking-[0.2em] uppercase text-blue-400 mb-2">New Messages</p>
          <p className="font-mc-b text-sm text-zinc-300">You have {data.messages.filter(m => m.sender === "admin" && !m.read).length} unread message{data.messages.filter(m => m.sender === "admin" && !m.read).length !== 1 ? "s" : ""}</p>
        </div>
      )}

      {/* Session Summaries */}
      {data.summaries?.length > 0 && (
        <div>
          <h3 className="font-mc-b text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Session Summaries</h3>
          <div className="space-y-2">
            {data.summaries.slice(0, 2).map(s => (
              <div key={s.id} className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-4">
                <p className="font-mc-b text-sm text-zinc-300 line-clamp-2">{s.client_summary}</p>
                <p className="font-mc-b text-[10px] text-zinc-600 mt-1">{new Date(s.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/book" className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 hover:border-[#D4AF37]/30 transition-all flex items-center gap-3" data-testid="quick-book">
          <Calendar size={20} className="text-[#D4AF37]" />
          <div>
            <p className="font-mc-b text-sm text-[#F5F5F0]">Book a Conversation</p>
            <p className="font-mc-b text-[10px] text-zinc-600">Find an available time</p>
          </div>
        </Link>
        <Link to="/mirror-conversations" className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 hover:border-[#D4AF37]/30 transition-all flex items-center gap-3">
          <BookOpen size={20} className="text-[#D4AF37]" />
          <div>
            <p className="font-mc-b text-sm text-[#F5F5F0]">Mirror Conversations</p>
            <p className="font-mc-b text-[10px] text-zinc-600">Learn more</p>
          </div>
        </Link>
      </div>

      {/* Disclaimer */}
      <p className="font-mc-b text-[10px] text-zinc-700 text-center leading-relaxed mt-8">
        Mirror Conversations&trade; is not therapy, counseling, or mental health treatment. If you are experiencing an emergency, call 911 or contact 988.
      </p>
    </motion.div>
  );
}

/* ═══ MY WORKSHOPS ═══ */
function MyWorkshops() {
  const { client } = useContext(ClientCtx);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!client?.email) return;
    http.get(`/workshops/public/my-workshops?email=${encodeURIComponent(client.email)}`)
      .then(r => setRegs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [client?.email]);

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="text-[#D4AF37] animate-spin" /></div>;

  const upcoming = regs.filter(r => r.workshop_date >= new Date().toISOString().slice(0, 10));
  const past = regs.filter(r => r.workshop_date < new Date().toISOString().slice(0, 10));

  return (
    <div className="space-y-6" data-testid="portal-my-workshops">
      <h2 className="font-mc-h text-xl text-[#F5F5F0]">My Workshops</h2>

      {regs.length === 0 ? (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-10 text-center">
          <p className="font-mc-b text-sm text-zinc-400">No workshop registrations found.</p>
          <Link to="/workshops" className="inline-block mt-4 border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 rounded-full font-mc-b text-xs tracking-wide hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">Browse Workshops</Link>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div>
              <p className="font-mc-b text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-3">Upcoming</p>
              <div className="space-y-3">
                {upcoming.map(r => (
                  <Link key={r.id} to={`/workshops/${r.workshop_slug}?registration=${r.id}&status=${r.payment_status === "paid" ? "success" : "pending"}`}
                    className="block rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 hover:border-[#D4AF37]/20 transition-all" data-testid={`portal-ws-${r.id}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-[#F5F5F0] text-sm font-medium">{r.workshop_title}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                          <span className="flex items-center gap-1"><Calendar size={12} /> {r.workshop_date}</span>
                          <span className="flex items-center gap-1"><Clock size={12} /> {r.workshop_start_time}</span>
                          <span className="flex items-center gap-1"><MapPin size={12} /> {r.workshop_location}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.payment_status === "paid" ? "bg-green-900/20 text-green-400" : "bg-amber-900/20 text-amber-400"}`}>{r.payment_status === "paid" ? "Confirmed" : "Pending"}</span>
                        <p className="text-zinc-600 text-[10px] mt-1 font-mono">{r.confirmation_number}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div>
              <p className="font-mc-b text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3">Past</p>
              <div className="space-y-3">
                {past.map(r => (
                  <div key={r.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 opacity-60" data-testid={`portal-ws-past-${r.id}`}>
                    <p className="text-[#F5F5F0] text-sm">{r.workshop_title}</p>
                    <p className="text-zinc-600 text-xs mt-1">{r.workshop_date} · {r.seats} seat{r.seats > 1 ? "s" : ""} · {r.confirmation_number}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


/* ═══ APPOINTMENTS ═══ */
function Appointments() {
  const { authHeaders } = useContext(ClientCtx);
  const [apts, setApts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    http.get("/client/appointments", { headers: authHeaders })
      .then(r => setApts(r.data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const cancel = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) return;
    try {
      await http.post(`/client/appointments/${id}/cancel`, {}, { headers: authHeaders });
      toast.success("Appointment canceled");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not cancel"); }
  };

  if (loading) return <Loader2 className="text-[#D4AF37] animate-spin mx-auto mt-20" />;

  const upcoming = apts.filter(a => a.date >= new Date().toISOString().split("T")[0] && !["canceled","no_show"].includes(a.status));
  const past = apts.filter(a => a.date < new Date().toISOString().split("T")[0] || ["canceled","no_show","completed"].includes(a.status));

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-mc-h text-2xl text-[#F5F5F0]">Your Appointments</h2>
        <Link to="/book" className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2 rounded-full font-mc-b text-xs tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all" data-testid="book-new">Book New</Link>
      </div>

      {upcoming.length > 0 && (
        <div>
          <h3 className="font-mc-b text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Upcoming</h3>
          <div className="space-y-3">
            {upcoming.map(a => <AptCard key={a.id} apt={a} onCancel={() => cancel(a.id)} />)}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <h3 className="font-mc-b text-xs tracking-[0.2em] uppercase text-zinc-600 mb-3">Past</h3>
          <div className="space-y-2">
            {past.map(a => <AptCard key={a.id} apt={a} compact />)}
          </div>
        </div>
      )}

      {apts.length === 0 && (
        <div className="text-center py-16">
          <p className="font-mc-h text-xl text-zinc-600 mb-4">No appointments yet</p>
          <Link to="/book" className="text-[#D4AF37] font-mc-b text-sm underline underline-offset-4">Book your first conversation</Link>
        </div>
      )}
    </motion.div>
  );
}

/* ═══ PROFILE ═══ */
function Profile() {
  const { client, authHeaders, logout } = useContext(ClientCtx);
  const [form, setForm] = useState({ preferred_name: client.preferred_name || "", phone: client.phone || "", timezone: client.timezone || "America/New_York" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await http.patch("/client/me", form, { headers: authHeaders });
      toast.success("Profile updated");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await http.post("/client/me/change-password", pwForm, { headers: authHeaders });
      toast.success("Password changed");
      setPwForm({ current_password: "", new_password: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-6 max-w-2xl">
      <h2 className="font-mc-h text-2xl text-[#F5F5F0]">Your Profile</h2>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
        <p className="font-mc-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Account Info</p>
        <div className="font-mc-b text-sm text-zinc-400"><span className="text-zinc-600">Name: </span>{client.name}</div>
        <div className="font-mc-b text-sm text-zinc-400"><span className="text-zinc-600">Email: </span>{client.email}</div>

        <div className="space-y-3 pt-4 border-t border-white/[0.06]">
          <div>
            <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-1 block">Preferred Name</label>
            <input value={form.preferred_name} onChange={e => setForm({ ...form, preferred_name: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors" />
          </div>
          <div>
            <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-1 block">Phone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors" />
          </div>
          <button onClick={saveProfile} disabled={saving} className="bg-[#D4AF37] text-[#0A0A0A] px-6 py-2.5 rounded-full font-mc-b text-xs tracking-wide uppercase hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50" data-testid="save-profile">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
        <p className="font-mc-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Change Password</p>
        <form onSubmit={changePassword} className="space-y-3">
          <input type="password" placeholder="Current Password" value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-700 text-sm" />
          <input type="password" placeholder="New Password (min 6 chars)" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-700 text-sm" />
          <button type="submit" className="border border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 rounded-full font-mc-b text-xs tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all" data-testid="change-password">
            Update Password
          </button>
        </form>
      </div>
    </motion.div>
  );
}

/* ═══ FORMS ═══ */
function ClientForms() {
  const { authHeaders } = useContext(ClientCtx);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState(null);

  const load = () => {
    http.get("/forms/my", { headers: authHeaders })
      .then(r => setForms(r.data))
      .catch(() => toast.error("Failed to load forms"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <Loader2 className="text-[#D4AF37] animate-spin mx-auto mt-20" />;

  if (activeForm) return <FormFiller assignment={activeForm} onClose={() => { setActiveForm(null); load(); }} />;

  const pending = forms.filter(f => f.status !== "completed");
  const completed = forms.filter(f => f.status === "completed");

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-6">
      <h2 className="font-mc-h text-2xl text-[#F5F5F0]">Your Forms & Agreements</h2>

      {pending.length > 0 && (
        <div>
          <h3 className="font-mc-b text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Action Required</h3>
          <div className="space-y-3">
            {pending.map(f => (
              <div key={f.id} className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/[0.03] p-5 flex items-center justify-between gap-4" data-testid={`form-pending-${f.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mc-b uppercase tracking-wider text-amber-400 bg-amber-900/20 px-2 py-0.5 rounded-full">{f.template_type}</span>
                    {f.due_date && <span className="text-[10px] text-zinc-500">Due {f.due_date}</span>}
                  </div>
                  <h4 className="font-mc-h text-lg text-[#F5F5F0]">{f.template_title}</h4>
                  <p className="font-mc-b text-xs text-zinc-500 mt-1">Assigned {new Date(f.assigned_at).toLocaleDateString()}</p>
                </div>
                <button onClick={() => setActiveForm(f)} data-testid={`fill-form-${f.id}`}
                  className="shrink-0 bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 rounded-full font-mc-b text-xs tracking-wide uppercase hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all flex items-center gap-2">
                  <PenLine size={12} /> Fill Out
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {completed.length > 0 && (
        <div>
          <h3 className="font-mc-b text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Completed</h3>
          <div className="space-y-2">
            {completed.map(f => (
              <div key={f.id} className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-4 flex items-center justify-between gap-4" data-testid={`form-completed-${f.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Check size={14} className="text-green-400 shrink-0" />
                  <span className="font-mc-b text-sm text-zinc-400 truncate">{f.template_title}</span>
                  <span className="font-mc-b text-[10px] text-zinc-600">{f.completed_at ? new Date(f.completed_at).toLocaleDateString() : ""}</span>
                </div>
                <span className="font-mc-b text-[10px] text-green-400 uppercase tracking-wider">Completed</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {forms.length === 0 && (
        <div className="text-center py-16">
          <FileText size={32} className="text-zinc-700 mx-auto mb-4" />
          <p className="font-mc-h text-xl text-zinc-600 mb-2">No forms assigned</p>
          <p className="font-mc-b text-sm text-zinc-700">When Mickey assigns you a form or agreement, it will appear here.</p>
        </div>
      )}
    </motion.div>
  );
}

/* ═══ GOALS ═══ */
function ClientGoals() {
  const { authHeaders } = useContext(ClientCtx);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    http.get("/client/goals", { headers: authHeaders })
      .then(r => setGoals(r.data))
      .catch(() => toast.error("Failed to load goals"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggle = async (goalId, stepId) => {
    try {
      const { data } = await http.post(`/client/goals/${goalId}/toggle/${stepId}`, {}, { headers: authHeaders });
      setGoals(prev => prev.map(g => g.id === goalId ? data : g));
    } catch { toast.error("Failed to update"); }
  };

  if (loading) return <Loader2 className="text-[#D4AF37] animate-spin mx-auto mt-20" />;

  const active = goals.filter(g => g.status === "active");
  const completed = goals.filter(g => g.status === "completed");

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-6">
      <h2 className="font-mc-h text-2xl text-[#F5F5F0]">Your Goals</h2>

      {active.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-mc-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Active Goals</h3>
          {active.map(g => (
            <div key={g.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3" data-testid={`goal-${g.id}`}>
              <h4 className="font-mc-h text-lg text-[#F5F5F0]">{g.title}</h4>
              {g.description && <p className="font-mc-b text-sm text-zinc-400">{g.description}</p>}
              {g.action_steps?.length > 0 && (
                <div className="space-y-2 pt-2">
                  {g.action_steps.map(s => (
                    <button key={s.id} onClick={() => toggle(g.id, s.id)} data-testid={`step-${s.id}`}
                      className="flex items-center gap-3 w-full text-left group">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all shrink-0 ${
                        s.completed ? "bg-[#D4AF37] border-[#D4AF37]" : "border-zinc-600 group-hover:border-[#D4AF37]/50"
                      }`}>
                        {s.completed && <Check size={12} className="text-[#0A0A0A]" />}
                      </div>
                      <span className={`font-mc-b text-sm transition-all ${s.completed ? "text-zinc-600 line-through" : "text-zinc-300"}`}>{s.text}</span>
                    </button>
                  ))}
                  <div className="flex items-center gap-2 pt-2">
                    <div className="flex-1 h-1 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${(g.action_steps.filter(s => s.completed).length / g.action_steps.length) * 100}%` }} />
                    </div>
                    <span className="font-mc-b text-[10px] text-zinc-500">{g.action_steps.filter(s => s.completed).length}/{g.action_steps.length}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {completed.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-mc-b text-xs tracking-[0.2em] uppercase text-zinc-500">Completed</h3>
          {completed.map(g => (
            <div key={g.id} className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-4 flex items-center gap-3">
              <Check size={16} className="text-green-400 shrink-0" />
              <span className="font-mc-b text-sm text-zinc-500">{g.title}</span>
            </div>
          ))}
        </div>
      )}

      {goals.length === 0 && (
        <div className="text-center py-16">
          <Target size={32} className="text-zinc-700 mx-auto mb-4" />
          <p className="font-mc-h text-xl text-zinc-600 mb-2">No goals yet</p>
          <p className="font-mc-b text-sm text-zinc-700">Goals and action steps from your sessions will appear here.</p>
        </div>
      )}
    </motion.div>
  );
}

/* ═══ RESOURCES ═══ */
function ClientResources() {
  const { authHeaders } = useContext(ClientCtx);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/client/resources", { headers: authHeaders })
      .then(r => setResources(r.data))
      .catch(() => toast.error("Failed to load resources"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="text-[#D4AF37] animate-spin mx-auto mt-20" />;

  const TYPE_ICON = { pdf: FileText, video: BookOpen, link: ArrowRight, worksheet: FileText };

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-6">
      <h2 className="font-mc-h text-2xl text-[#F5F5F0]">Your Resources</h2>

      {resources.length > 0 ? (
        <div className="space-y-3">
          {resources.map(r => {
            const Icon = TYPE_ICON[r.type] || FileText;
            return (
              <div key={r.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 flex items-center justify-between gap-4" data-testid={`resource-${r.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-[#D4AF37]" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-mc-b text-sm text-[#F5F5F0] truncate">{r.title}</h4>
                    {r.description && <p className="font-mc-b text-xs text-zinc-500 truncate">{r.description}</p>}
                    <span className="font-mc-b text-[10px] text-zinc-600 uppercase">{r.type}</span>
                  </div>
                </div>
                {r.url && (
                  <a href={r.url} target="_blank" rel="noreferrer" data-testid={`open-resource-${r.id}`}
                    className="shrink-0 border border-[#D4AF37] text-[#D4AF37] px-4 py-2 rounded-full font-mc-b text-xs tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">
                    Open
                  </a>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <BookOpen size={32} className="text-zinc-700 mx-auto mb-4" />
          <p className="font-mc-h text-xl text-zinc-600 mb-2">No resources yet</p>
          <p className="font-mc-b text-sm text-zinc-700">Worksheets, links, and materials from your sessions will appear here.</p>
        </div>
      )}
    </motion.div>
  );
}

/* ═══ MESSAGES ═══ */
function ClientMessages() {
  const { authHeaders, client } = useContext(ClientCtx);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = () => {
    http.get("/client/messages", { headers: authHeaders })
      .then(r => setMessages(r.data))
      .catch(() => toast.error("Failed to load messages"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await http.post("/client/messages", { text: text.trim() }, { headers: authHeaders });
      setText("");
      load();
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  if (loading) return <Loader2 className="text-[#D4AF37] animate-spin mx-auto mt-20" />;

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-4">
      <h2 className="font-mc-h text-2xl text-[#F5F5F0]">Messages</h2>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] max-h-[450px] overflow-y-auto p-5 space-y-3" data-testid="messages-thread">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare size={28} className="text-zinc-700 mx-auto mb-3" />
            <p className="font-mc-b text-sm text-zinc-600">No messages yet. Send a message to start a conversation.</p>
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
              m.sender === "client"
                ? "bg-[#D4AF37]/15 text-[#F5F5F0] rounded-br-sm"
                : "bg-white/[0.05] text-zinc-300 rounded-bl-sm"
            }`} data-testid={`msg-${m.id}`}>
              {m.sender === "admin" && <p className="font-mc-b text-[10px] text-[#D4AF37] mb-1">{m.sender_name || "Mickey"}</p>}
              <p className="font-mc-b text-sm whitespace-pre-wrap">{m.text}</p>
              <p className="font-mc-b text-[10px] text-zinc-600 mt-1.5">{new Date(m.created_at).toLocaleString()}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Type a message..."
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
          className="flex-1 bg-transparent border border-white/10 rounded-full px-5 py-3 font-mc-b text-sm text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-700"
          data-testid="client-msg-input" />
        <button onClick={send} disabled={sending || !text.trim()} data-testid="client-msg-send"
          className="bg-[#D4AF37] text-[#0A0A0A] px-5 py-3 rounded-full font-mc-b text-sm hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50 flex items-center gap-2">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>

      <p className="font-mc-b text-[10px] text-zinc-700 text-center">
        Messages are private between you and Mickey.
      </p>
    </motion.div>
  );
}

/* ═══ FORM FILLER ═══ */
function FormFiller({ assignment, onClose }) {
  const { authHeaders } = useContext(ClientCtx);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [signatureName, setSignatureName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    http.get(`/forms/my/${assignment.id}`, { headers: authHeaders })
      .then(r => {
        setFull(r.data);
        // Pre-fill if there are existing responses
        if (r.data.responses) setResponses(r.data.responses);
      })
      .catch(() => toast.error("Failed to load form"))
      .finally(() => setLoading(false));
  }, [assignment.id]);

  if (loading) return <Loader2 className="text-[#D4AF37] animate-spin mx-auto mt-20" />;
  if (!full || !full.template) return null;

  const template = full.template;

  const validate = () => {
    const errs = {};
    for (const field of template.fields || []) {
      if (field.required) {
        const val = responses[field.id];
        if (val === undefined || val === null || (typeof val === "string" && !val.trim())) {
          errs[field.id] = `${field.label} is required`;
        }
      }
    }
    if (template.requires_signature) {
      if (!acknowledged) errs._ack = "You must acknowledge the agreement";
      if (!signatureName.trim()) errs._sig = "Please type your full name";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (!validate()) { toast.error("Please complete all required fields"); return; }
    setSubmitting(true);
    try {
      await http.post(`/forms/my/${assignment.id}/submit`, {
        responses,
        signature_name: signatureName,
        acknowledged,
      }, { headers: authHeaders });
      toast.success("Form submitted successfully");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const updateResponse = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) setErrors(prev => { const n = { ...prev }; delete n[fieldId]; return n; });
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-6 max-w-2xl" data-testid="form-filler">
      <div className="flex items-center justify-between">
        <div>
          <span className={`text-[10px] font-mc-b uppercase tracking-wider px-2 py-0.5 rounded-full ${
            template.type === "agreement" ? "bg-amber-900/30 text-amber-400" : "bg-blue-900/30 text-blue-400"
          }`}>{template.type}</span>
          <h2 className="font-mc-h text-2xl text-[#F5F5F0] mt-2">{template.title}</h2>
          {template.description && <p className="font-mc-b text-sm text-zinc-400 mt-1">{template.description}</p>}
        </div>
        <button onClick={onClose} className="text-zinc-600 hover:text-zinc-400 transition-colors"><X size={18} /></button>
      </div>

      <div className="space-y-5">
        {(template.fields || []).map(field => (
          <div key={field.id} className="space-y-1.5" data-testid={`form-field-${field.id}`}>
            <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-400 block">
              {field.label} {field.required && <span className="text-amber-500">*</span>}
            </label>
            {field.helper_text && <p className="font-mc-b text-[10px] text-zinc-600">{field.helper_text}</p>}

            {field.type === "text" && (
              <input value={responses[field.id] || ""} onChange={e => updateResponse(field.id, e.target.value)}
                placeholder={field.placeholder}
                className={`w-full bg-transparent border-b ${errors[field.id] ? "border-red-500" : "border-white/10"} text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors`} />
            )}
            {field.type === "email" && (
              <input type="email" value={responses[field.id] || ""} onChange={e => updateResponse(field.id, e.target.value)}
                placeholder={field.placeholder}
                className={`w-full bg-transparent border-b ${errors[field.id] ? "border-red-500" : "border-white/10"} text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors`} />
            )}
            {field.type === "phone" && (
              <input type="tel" value={responses[field.id] || ""} onChange={e => updateResponse(field.id, e.target.value)}
                placeholder={field.placeholder}
                className={`w-full bg-transparent border-b ${errors[field.id] ? "border-red-500" : "border-white/10"} text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors`} />
            )}
            {field.type === "number" && (
              <input type="number" value={responses[field.id] || ""} onChange={e => updateResponse(field.id, e.target.value)}
                placeholder={field.placeholder}
                className={`w-full bg-transparent border-b ${errors[field.id] ? "border-red-500" : "border-white/10"} text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors`} />
            )}
            {field.type === "date" && (
              <input type="date" value={responses[field.id] || ""} onChange={e => updateResponse(field.id, e.target.value)}
                className={`w-full bg-transparent border-b ${errors[field.id] ? "border-red-500" : "border-white/10"} text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors`} />
            )}
            {field.type === "textarea" && (
              <textarea value={responses[field.id] || ""} onChange={e => updateResponse(field.id, e.target.value)}
                placeholder={field.placeholder} rows={3}
                className={`w-full bg-transparent border ${errors[field.id] ? "border-red-500" : "border-white/10"} rounded-lg px-3 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors resize-none`} />
            )}
            {field.type === "select" && (
              <select value={responses[field.id] || ""} onChange={e => updateResponse(field.id, e.target.value)}
                className={`w-full bg-[#0A0A0A] border-b ${errors[field.id] ? "border-red-500" : "border-white/10"} text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors`}>
                <option value="">Select...</option>
                {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            )}
            {field.type === "radio" && (
              <div className="space-y-2 pt-1">
                {(field.options || []).map(opt => (
                  <label key={opt} className="flex items-center gap-3 cursor-pointer">
                    <input type="radio" name={`field_${field.id}`} value={opt}
                      checked={responses[field.id] === opt}
                      onChange={() => updateResponse(field.id, opt)}
                      className="accent-[#D4AF37]" />
                    <span className="font-mc-b text-sm text-zinc-300">{opt}</span>
                  </label>
                ))}
              </div>
            )}
            {field.type === "checkbox" && (
              <label className="flex items-center gap-3 cursor-pointer pt-1">
                <input type="checkbox" checked={!!responses[field.id]}
                  onChange={e => updateResponse(field.id, e.target.checked)}
                  className="accent-[#D4AF37]" />
                <span className="font-mc-b text-sm text-zinc-300">{field.placeholder || "Yes"}</span>
              </label>
            )}

            {errors[field.id] && (
              <p className="font-mc-b text-[10px] text-red-400 flex items-center gap-1"><AlertCircle size={10} /> {errors[field.id]}</p>
            )}
          </div>
        ))}
      </div>

      {/* Signature Section */}
      {template.requires_signature && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.02] p-6 space-y-4" data-testid="signature-section">
          <p className="font-mc-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Acknowledgment & Signature</p>
          {template.acknowledgment_text && (
            <p className="font-mc-b text-sm text-zinc-300 leading-relaxed italic">&ldquo;{template.acknowledgment_text}&rdquo;</p>
          )}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={acknowledged} onChange={e => {
              setAcknowledged(e.target.checked);
              if (errors._ack) setErrors(prev => { const n = { ...prev }; delete n._ack; return n; });
            }}
              className="accent-[#D4AF37] mt-1" data-testid="acknowledge-checkbox" />
            <span className="font-mc-b text-sm text-zinc-300">I have read and agree to the above statement</span>
          </label>
          {errors._ack && <p className="font-mc-b text-[10px] text-red-400 flex items-center gap-1"><AlertCircle size={10} /> {errors._ack}</p>}

          <div>
            <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-400 block mb-1.5">Type Your Full Legal Name *</label>
            <input value={signatureName} onChange={e => {
              setSignatureName(e.target.value);
              if (errors._sig) setErrors(prev => { const n = { ...prev }; delete n._sig; return n; });
            }}
              placeholder="e.g. John Michael Smith"
              className={`w-full bg-transparent border-b ${errors._sig ? "border-red-500" : "border-white/10"} text-[#F5F5F0] py-3 focus:border-[#D4AF37] focus:outline-none transition-colors text-xl`}
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
              data-testid="signature-name-input" />
          </div>
          {errors._sig && <p className="font-mc-b text-[10px] text-red-400 flex items-center gap-1"><AlertCircle size={10} /> {errors._sig}</p>}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button onClick={submit} disabled={submitting} data-testid="submit-form-btn"
          className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-3.5 rounded-full font-mc-b tracking-wide uppercase text-sm flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50">
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          {submitting ? "Submitting..." : "Submit"}
        </button>
        <button onClick={onClose} className="px-6 py-3.5 rounded-full font-mc-b text-xs text-zinc-500 border border-white/10 hover:border-zinc-600 transition-colors">
          Save for Later
        </button>
      </div>
    </motion.div>
  );
}

/* ═══ SHARED ═══ */
function AptCard({ apt, compact, onCancel }) {
  const statusColors = { requested: "text-yellow-400", confirmed: "text-green-400", pending_payment: "text-orange-400", completed: "text-blue-400", canceled: "text-red-400", "no_show": "text-zinc-500" };

  const to12h = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const ampm = h < 12 ? "AM" : "PM";
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
  };

  if (compact) {
    return (
      <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Calendar size={14} className="text-zinc-600 shrink-0" />
          <span className="font-mc-b text-xs text-zinc-400 truncate">{apt.service_title}</span>
          <span className="font-mc-b text-[10px] text-zinc-600">{apt.date}</span>
          <span className="font-mc-b text-[10px] text-zinc-600">{to12h(apt.start_time)} EST</span>
        </div>
        <span className={`font-mc-b text-[10px] uppercase tracking-wider ${statusColors[apt.status] || "text-zinc-500"}`}>{apt.status}</span>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3" data-testid={`apt-card-${apt.id}`}>
      <div className="flex justify-between items-start">
        <h3 className="font-mc-h text-lg text-[#F5F5F0]">{apt.service_title}</h3>
        <span className={`font-mc-b text-[10px] uppercase tracking-wider ${statusColors[apt.status] || "text-zinc-500"}`}>{apt.status}</span>
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
        <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#D4AF37]" /> {new Date(apt.date + "T12:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>
        <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#D4AF37]" /> {to12h(apt.start_time)} – {to12h(apt.end_time)} EST</span>
        <span className="flex items-center gap-1.5">{apt.format === "office" ? <MapPin size={14} className="text-[#D4AF37]" /> : <Video size={14} className="text-[#D4AF37]" />} {apt.format === "office" ? "In Office" : "Zoom"}</span>
      </div>
      {apt.format === "zoom" && apt.zoom_link && apt.status === "confirmed" && (
        <a href={apt.zoom_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#D4AF37] font-mc-b text-xs underline underline-offset-4"><Video size={12} /> Join Zoom Meeting</a>
      )}
      {onCancel && !["canceled","completed","no_show"].includes(apt.status) && (
        <button onClick={onCancel} className="text-zinc-600 hover:text-red-400 font-mc-b text-xs transition-colors" data-testid={`cancel-apt-${apt.id}`}>Cancel Appointment</button>
      )}
    </div>
  );
}

function PageWrap({ children }) {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        .font-mc-h { font-family: 'Cormorant Garamond', serif; }
        .font-mc-b { font-family: 'DM Sans', sans-serif; }
      `}</style>
      <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-20 px-6">{children}</div>
    </>
  );
}
